import { Sparkle } from "@phosphor-icons/react";
import { formatCurrency, formatCurrencyExact } from "../../lib/romEngine";
import { Row } from "./primitives";
import { addDaysISO, fmtDateShort } from "./helpers";

export function EmptyTotalCard({ inputs }) {
    const checks = [
        { key: "Primary loss", done: !!inputs.lossType },
        { key: "Total sq ft", done: !!inputs.sqft && Number(inputs.sqft) > 0 },
        { key: "Scope scale", done: !!inputs.scope },
    ];
    const done = checks.filter((c) => c.done).length;
    const pct = (done / checks.length) * 100;
    return (
        <div className="rom-card border-t-4 border-t-[var(--rom-border-strong)]" data-testid="rom-empty-total">
            <div className="px-5 py-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold">
                    Total ROM Estimate
                </div>
                <div className="font-display font-black text-4xl tracking-tighter text-[var(--rom-text-3)] mt-1">
                    —
                </div>
                <div className="mt-4 p-3 rounded-[4px] bg-slate-50 border border-dashed border-[var(--rom-border-strong)] text-xs text-[var(--rom-text-2)] leading-relaxed">
                    <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-[var(--rom-text)]">Quick ROM inputs</span>
                        <span className="font-mono font-bold tabular text-[var(--rom-navy)]" data-testid="rom-progress-count">
                            {done} of {checks.length}
                        </span>
                    </div>
                    <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden" data-testid="rom-progress-bar">
                        <div
                            className="h-full bg-[var(--rom-orange)] rounded-full transition-all duration-300"
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                        {checks.map((c) => (
                            <span
                                key={c.key}
                                className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${c.done ? "bg-[var(--rom-green-soft,#dcfce7)] text-[var(--rom-green-dark,#166534)] line-through opacity-70" : "bg-amber-100 text-amber-800"}`}
                                data-testid={`rom-progress-${c.done ? "done" : "todo"}`}
                            >
                                {c.key}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export function TotalCard({ result, inputs, ref_ }) {
    const total = result.grand_total;
    const bL = result.band_low, bH = result.band_high;
    // Data-quality / unvalidated-assumption flags are never shown as a banner here —
    // they surface only as a small neutral chip in the Inputs-used panel. A banner on
    // this card is reserved for things the user can actually act on (missing address,
    // missing sqft, conflicting input), which this card doesn't own.
    const notes = (result.assumptions || []).filter((a) => !a.unvalidated);
    const parts = [
        { label: "Labour", v: result.labour_total, color: "var(--rom-navy)" },
        { label: "Consumables", v: result.consumables_total, color: "#94a3b8" },
        { label: "Equipment", v: result.equipment_total, color: "var(--rom-orange)" },
        { label: "Subcontractors", v: result.subs_total || 0, color: "var(--rom-green)" },
        { label: "Misc / Incidentals", v: result.incidentals_total, color: "#a855f7" },
    ].filter((p) => p.v > 0 || ["Labour","Equipment","Misc / Incidentals"].includes(p.label));
    return (
        <div className="rom-card border-t-4 border-t-[var(--rom-orange)]" data-testid="rom-total-card">
            <div className="px-5 pt-5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-2)] font-semibold" title="A ROM is a directional ceiling, not a fixed contract price">
                    Total ROM Estimate · Not-to-Exceed
                </div>
                <div className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[var(--rom-navy)] mt-1"
                     data-testid="rom-grand-total">
                    {formatCurrency(total)}
                </div>
                {result.tax_rate_pct > 0 && (
                    <div className="mt-1.5 space-y-0.5" data-testid="rom-tax-breakdown">
                        <div className="flex items-center justify-between text-xs text-[var(--rom-text-2)]">
                            <span>Tax ({(result.tax_rate_pct * 100).toFixed(1)}%)</span>
                            <span className="font-mono">{formatCurrencyExact(result.tax_total)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm font-bold text-[var(--rom-text)]">
                            <span>Total incl. tax</span>
                            <span className="font-mono" data-testid="rom-total-with-tax">{formatCurrencyExact(result.total_with_tax)}</span>
                        </div>
                    </div>
                )}
                <div className="flex items-center gap-2 mt-2 text-sm">
                    <span className="chip chip-green" data-testid="rom-confidence-band">±{result.confidence_pct}%</span>
                    {result.band_source === "calibrated" && (
                        <span className="chip chip-navy" title="Band tightened from this loss type's closed-job accuracy" data-testid="rom-band-calibrated">calibrated</span>
                    )}
                    <span className="text-[var(--rom-text-2)] tabular">
                        {formatCurrency(bL)} – {formatCurrency(bH)}
                    </span>
                </div>
            </div>
            <div className="px-5 pt-4">
                <div className="h-3 w-full bg-slate-100 rounded-sm overflow-hidden flex">
                    {parts.map((p) => (
                        <div key={p.label}
                            style={{ width: `${(p.v / total) * 100}%`, background: p.color }}
                            title={`${p.label}: ${formatCurrency(p.v)}`}
                        />
                    ))}
                </div>
                <div className="mt-3 space-y-2 pb-5">
                    {parts.map((p) => (
                        <div key={p.label} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
                                <span className="text-[var(--rom-text-2)]">{p.label}</span>
                            </div>
                            <span className="font-mono font-semibold tabular">{formatCurrencyExact(p.v)}</span>
                        </div>
                    ))}
                </div>
            </div>
            <div className="border-t border-[var(--rom-border)] px-5 py-4 bg-slate-50 rounded-b-[6px] text-xs space-y-1.5 text-[var(--rom-text-2)]">
                <div className="flex items-center gap-1.5 font-semibold text-[var(--rom-text)] mb-1">
                    <Sparkle size={12} weight="fill" className="text-[var(--rom-orange)]" /> Crew &amp; timing
                </div>
                <Row k="Implied crew" v={`${result.crew_size} people`} />
                <Row k="Field days" v={`${result.days} d`} />
                <Row k="Labour hours" v={`${result.field_hours_total} hrs`} />
                <Row k="O&P markup" v={`${(result.breakdown_inputs.op_markup * 100 - 100).toFixed(0)}%`} />
                <Row k="Loss type" v={inputs.lossType} />
                {result.secondary_loss && <Row k="Secondary loss" v={result.secondary_loss === "other" ? (inputs.secondaryLossOther || "other") : result.secondary_loss} />}
                <Row k="Prime rates" v={inputs.primeArea ? "Yes" : "No"} />
                {inputs.projectStartDate && <Row k="Start date" v={fmtDateShort(inputs.projectStartDate)} />}
                {inputs.projectStartDate && <Row k="Finish date" v={fmtDateShort(addDaysISO(inputs.projectStartDate, (result.timeline_days || result.days) - 1))} />}
                <div className="text-[10px] text-[var(--rom-text-3)] pt-1 italic">Implied crew is derived from labour hours ÷ duration — a planning output, not a cost lever.</div>
            </div>
            {notes.length > 0 && (
                <details className="border-t border-[var(--rom-border)] px-5 py-3 group" data-testid="rom-assumptions">
                    <summary className="flex items-center justify-between cursor-pointer list-none text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--rom-text-2)] hover:text-[var(--rom-text)]">
                        <span>Assumptions &amp; notes</span>
                        <span className="font-mono tabular text-[var(--rom-orange)] group-open:rotate-180 transition-transform">▾ {notes.length}</span>
                    </summary>
                    <div className="mt-3 space-y-2.5">
                        {notes.map((a) => (
                            <div key={a.key} className="text-xs leading-relaxed" data-testid={`rom-assumption-${a.key}`}>
                                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                    <span className="font-semibold text-[var(--rom-text)]">{a.label}</span>
                                    <span className="font-mono tabular text-[var(--rom-navy)]">{a.value}</span>
                                </div>
                                <div className="text-[var(--rom-text-2)] mt-0.5">{a.note}</div>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}
